import { useState, useEffect, useRef } from "react";
import Logo from './logo.png';
/* ── Tailwind is not available in this env; we use inline styles + a <style> tag ── */

const LOGO_URL = Logo;

/* ─────────────────────────────────────────────
   Utility: useReveal — adds "visible" class via IntersectionObserver
───────────────────────────────────────────── */
function useReveal() {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    obs.unobserve(el);
                }
            },
            { threshold: 0.1, rootMargin: "-60px 0px" }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return { ref, visible };
}

function Reveal({ children, delay = 0, instant = false }) {
    const { ref, visible: observed } = useReveal();
    const visible = instant || observed;
    return (
        <div
            ref={ref}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: `opacity 0.8s cubic-bezier(.22,1,.36,1) ${delay}s, transform 0.8s cubic-bezier(.22,1,.36,1) ${delay}s`,
            }}
        >
            {children}
        </div>
    );
}

/* ─────────────────────────────────────────────
   Pill badge
───────────────────────────────────────────── */
function Pill({ children }) {
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                borderRadius: "9999px",
                padding: "0.4rem 0.9rem",
                background: "oklch(0.55 0.24 300 / 0.08)",
                color: "oklch(0.4 0.18 300)",
                fontSize: "0.75rem",
                fontWeight: 500,
                border: "1px solid oklch(0.55 0.24 300 / 0.12)",
            }}
        >
            {children}
        </span>
    );
}

/* ─────────────────────────────────────────────
   Pulsing dot
───────────────────────────────────────────── */
function PingDot() {
    return (
        <span style={{ position: "relative", display: "inline-flex", width: "0.375rem", height: "0.375rem" }}>
            <span className="consortia-ping" style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "oklch(0.55 0.24 300)", opacity: 0.75 }} />
            <span style={{ position: "relative", display: "inline-flex", width: "100%", height: "100%", borderRadius: "50%", background: "oklch(0.55 0.24 300)" }} />
        </span>
    );
}

/* ─────────────────────────────────────────────
   Nav
───────────────────────────────────────────── */
function Nav() {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 16);
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, []);

    const navInner = {
        display: "flex",
        width: "100%",
        maxWidth: "72rem",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.625rem 1rem",
        borderRadius: "9999px",
        transition: "all 0.5s",
        ...(scrolled
            ? {
                background: "oklch(1 0 0 / 0.7)",
                backdropFilter: "blur(16px) saturate(160%)",
                WebkitBackdropFilter: "blur(16px) saturate(160%)",
                border: "1px solid oklch(0.55 0.24 300 / 0.08)",
                boxShadow: "0 8px 30px -12px oklch(0.4 0.15 295 / 0.2)",
            }
            : {}),
    };

    return (
        <div style={{ position: "fixed", inset: "0 0 auto 0", zIndex: 50, display: "flex", justifyContent: "center", padding: "1rem" }}>
            <div style={navInner}>
                <a href="#top">
                    <img src={LOGO_URL} alt="Consortia" style={{ height: window.innerWidth < 768 ? "2.75rem" : "4.5rem", margin: 0, mixBlendMode: "multiply" }} />
                </a>
                <nav className="consortia-nav-links">
                    {[
                        { href: "#about", label: "About" },
                        { href: "#early-access", label: "Access" },
                        { href: "mailto:connect@customersuccessconsortia.com", label: "Contact" },
                    ].map((item) => {
                        const { href, label } = item;
                        return (
                            <a key={label} href={href} style={{ fontSize: "0.875rem", fontWeight: 500, color: "oklch(0.18 0.04 285 / 0.7)", transition: "color 0.3s", textDecoration: "none" }}
                                onMouseEnter={e => e.target.style.color = "oklch(0.18 0.04 285)"}
                                onMouseLeave={e => e.target.style.color = "oklch(0.18 0.04 285 / 0.7)"}
                            >{label}</a>
                        );
                    })}
                </nav>
                <a href="#early-access" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", height: window.innerWidth < 768 ? "2.2rem" : "2.5rem", padding: window.innerWidth < 768 ? "0 0.9rem" : "0 1.25rem", background: "linear-gradient(135deg, oklch(0.55 0.24 300), oklch(0.65 0.27 325))", color: "white", boxShadow: "0 12px 40px -8px oklch(0.55 0.25 305 / 0.45)", borderRadius: "9999px", fontSize: window.innerWidth < 768 ? "0.75rem" : "0.8125rem", fontWeight: 600, textDecoration: "none", transition: "all 0.4s cubic-bezier(.22,1,.36,1)" }}>
                    Request Access
                </a>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────
   Hero
───────────────────────────────────────────── */
function Hero() {
    const [show, setShow] = useState(false);
    useEffect(() => { setTimeout(() => setShow(true), 100); }, []);

    return (
        <section id="top" style={{ position: "relative", isolation: "isolate", overflow: "hidden", minHeight: window.innerWidth < 768 ? "auto" : "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: window.innerWidth < 768 ? "5.5rem 1rem 3rem" : "8rem 1.5rem 6rem", textAlign: "center", backgroundImage: "radial-gradient(ellipse 70% 50% at 20% 0%, oklch(0.92 0.06 305 / 0.6), transparent 70%), radial-gradient(ellipse 60% 50% at 90% 20%, oklch(0.9 0.07 320 / 0.5), transparent 70%), radial-gradient(ellipse 80% 60% at 50% 100%, oklch(0.93 0.05 295 / 0.5), transparent 70%)" }}>
            {/* Blobs */}
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                <div className="consortia-float-slow" style={{ position: "absolute", top: "-8rem", left: "25%", width: "36rem", height: "36rem", borderRadius: "50%", background: "radial-gradient(circle, oklch(0.7 0.22 305 / 0.25), transparent 70%)", filter: "blur(80px)" }} />
                <div className="consortia-float-slower" style={{ position: "absolute", top: "33%", right: "-10rem", width: "32rem", height: "32rem", borderRadius: "50%", background: "radial-gradient(circle, oklch(0.68 0.27 330 / 0.22), transparent 70%)", filter: "blur(80px)" }} />
            </div>

            <div style={{ position: "relative", zIndex: 10, maxWidth: "64rem", width: "100%", margin: "0 auto" }}>
                <Reveal instant={show}>
                    <img src={LOGO_URL} alt="Consortia" style={{ height: "5rem", width: "auto", margin: "0 auto 2.5rem" }} />
                </Reveal>

                <Reveal instant={show} delay={0.1}>
                    <Pill><PingDot /> Pre-launch · Coming Soon</Pill>
                </Reveal>

                <Reveal instant={show} delay={0.2}>
                    <h1 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "clamp(2.5rem, 6vw, 4.5rem)", fontWeight: 700, lineHeight: 1.05, letterSpacing: "-0.025em", marginTop: "1.5rem" }}>
                        Consortia is <br />
                        <span style={{ background: "linear-gradient(135deg, oklch(0.5 0.25 300) 0%, oklch(0.65 0.27 325) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                            Coming Soon.
                        </span>
                    </h1>
                </Reveal>

                <Reveal instant={show} delay={0.35}>
                    <p style={{ maxWidth: "42rem", margin: "1.75rem auto 0", fontSize: "clamp(0.875rem, 1.5vw, 1.125rem)", lineHeight: 1.65, color: "oklch(0.5 0.04 285)" }}>
                        A modern community for customer success, retention, revenue, and growth professionals.
                    </p>
                </Reveal>

                <Reveal instant={show} delay={0.5}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "0.75rem", marginTop: "2.5rem" }}>
                        <a href="#early-access" style={{ display: "inline-flex", alignItems: "center", height: "3rem", padding: "0 1.75rem", background: "linear-gradient(135deg, oklch(0.55 0.24 300), oklch(0.65 0.27 325))", color: "white", boxShadow: "0 12px 40px -8px oklch(0.55 0.25 305 / 0.45)", borderRadius: "9999px", fontSize: "0.875rem", fontWeight: 600, textDecoration: "none", gap: "0.5rem" }}>
                            Request Early Access
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </a>
                        <a href="#about" style={{ display: "inline-flex", alignItems: "center", height: "3rem", padding: "0 1.75rem", background: "oklch(1 0 0 / 0.6)", border: "1px solid oklch(0.55 0.24 300 / 0.2)", color: "oklch(0.4 0.18 300)", borderRadius: "9999px", fontSize: "0.875rem", fontWeight: 600, textDecoration: "none" }}>
                            Learn More
                        </a>
                    </div>
                </Reveal>

                <Reveal instant={show} delay={0.7}>
                    <p style={{ marginTop: "2.5rem", fontSize: "0.875rem", color: "oklch(0.5 0.04 285 / 0.8)" }}>
                        Built for the people shaping the future of customer success.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────
   About
───────────────────────────────────────────── */
function About() {
    return (
        <section id="about" style={{ padding: "7rem 1.5rem" }}>
            <div style={{ maxWidth: "48rem", margin: "0 auto", textAlign: "center" }}>
                <Reveal>
                    <Pill>About</Pill>
                    <h2 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "clamp(1.75rem, 4vw, 3rem)", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.025em", marginTop: "1.5rem" }}>
                        One community.{" "}
                        <span style={{ background: "linear-gradient(135deg, oklch(0.5 0.25 300) 0%, oklch(0.65 0.27 325) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                            Many voices.
                        </span>
                    </h2>
                    <p style={{ maxWidth: "42rem", margin: "1.75rem auto 0", fontSize: "clamp(0.875rem, 1.5vw, 1.125rem)", lineHeight: 1.65, color: "oklch(0.5 0.04 285)" }}>
                        Consortia is an independent professional community created to bring together customer success leaders, revenue professionals, success operators, account managers, founders, and growth-focused teams.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────
   Why Consortia
───────────────────────────────────────────── */
const WHY_CARDS = [
    { n: "01", title: "Meaningful Networking", body: "Connect with operators and leaders solving the same problems as you — beyond surface-level intros." },
    { n: "02", title: "Industry Conversations", body: "Frank, off-the-record exchanges on retention, expansion, and what's next for the role." },
    { n: "03", title: "Growth-Focused Community", body: "An ecosystem built around outcomes — career growth, company growth, and customer growth." },
    { n: "04", title: "Events & Roundtables", body: "Intimate gatherings, online and in person, designed for depth — not for badges." },
    { n: "05", title: "Knowledge Sharing", body: "Playbooks, frameworks, and templates shaped by the community itself." },
    { n: "06", title: "Professional Opportunities", body: "Roles, partnerships, and collaborations that find you through trusted peers." },
];

function WhyCard({ n, title, body, delay }) {
    const [hovered, setHovered] = useState(false);
    return (
        <Reveal delay={delay}>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{ background: "oklch(1 0 0 / 0.7)", border: `1px solid oklch(0.55 0.24 300 / ${hovered ? "0.25" : "0.08"})`, borderRadius: "1rem", padding: "1.75rem", boxShadow: hovered ? "0 20px 50px -12px oklch(0.4 0.2 295 / 0.25)" : "0 4px 24px -8px oklch(0.4 0.15 295 / 0.12)", transform: hovered ? "translateY(-4px)" : "none", transition: "all 0.5s cubic-bezier(.22,1,.36,1)", height: "100%" }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "2.75rem", height: "2.75rem", borderRadius: "0.75rem", background: "linear-gradient(135deg, oklch(0.55 0.24 300), oklch(0.65 0.27 325))", color: "white", fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "0.875rem", fontWeight: 700, boxShadow: "0 12px 40px -8px oklch(0.55 0.25 305 / 0.45)", marginBottom: "1.25rem" }}>
                    {n}
                </div>
                <h3 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "1.125rem", fontWeight: 600 }}>{title}</h3>
                <p style={{ marginTop: "0.625rem", fontSize: "0.875rem", lineHeight: 1.6, color: "oklch(0.5 0.04 285)" }}>{body}</p>
            </div>
        </Reveal>
    );
}

function WhyConsortia() {
    return (
        <section style={{ padding: "7rem 1.5rem" }}>
            <div style={{ maxWidth: "72rem", margin: "0 auto" }}>
                <div style={{ maxWidth: "42rem", margin: "0 auto 4rem", textAlign: "center" }}>
                    <Reveal>
                        <Pill>Why Consortia</Pill>
                        <h2 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "clamp(1.75rem, 4vw, 3rem)", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.025em", marginTop: "1.5rem" }}>
                            More than a network —{" "}
                            <span style={{ background: "linear-gradient(135deg, oklch(0.5 0.25 300) 0%, oklch(0.65 0.27 325) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                                a real ecosystem.
                            </span>
                        </h2>
                    </Reveal>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>
                    {WHY_CARDS.map((c, i) => <WhyCard key={c.n} {...c} delay={i * 0.06} />)}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────
   Who It's For
───────────────────────────────────────────── */
const WHO_ITEMS = [
    "Customer Success Professionals",
    "Revenue Leaders",
    "Account Managers",
    "SuccessOps Teams",
    "Founders",
    "Growth Professionals",
];

function WhoCard({ label, delay }) {
    const [hovered, setHovered] = useState(false);
    return (
        <Reveal delay={delay}>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", background: "oklch(1 0 0 / 0.7)", border: `1px solid oklch(0.55 0.24 300 / ${hovered ? "0.25" : "0.08"})`, borderRadius: "1rem", padding: "1.75rem 1.5rem", boxShadow: hovered ? "0 20px 50px -12px oklch(0.4 0.2 295 / 0.25)" : "0 4px 24px -8px oklch(0.4 0.15 295 / 0.12)", transform: hovered ? "translateY(-4px)" : "none", transition: "all 0.5s cubic-bezier(.22,1,.36,1)", cursor: "default" }}
            >
                <span style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "clamp(0.875rem, 1.2vw, 1.125rem)", fontWeight: 600 }}>{label}</span>
                {/* <span style={{ color: hovered ? "oklch(0.55 0.24 300)" : "oklch(0.55 0.24 300 / 0.4)", transition: "all 0.5s cubic-bezier(.22,1,.36,1)", transform: hovered ? "translateX(4px)" : "none", fontSize: "1.25rem" }}>→</span> */}
            </div>
        </Reveal>
    );
}

function WhoItsFor() {
    return (
        <section style={{ position: "relative", padding: "7rem 1.5rem" }}>
            <div style={{ position: "absolute", left: "50%", top: "2.5rem", width: "50rem", height: "28rem", transform: "translateX(-50%)", background: "radial-gradient(circle, oklch(0.7 0.22 305 / 0.25), transparent 70%)", filter: "blur(80px)", opacity: 0.4, borderRadius: "50%", pointerEvents: "none" }} />
            <div style={{ maxWidth: "72rem", margin: "0 auto", position: "relative", zIndex: 1 }}>
                <div style={{ maxWidth: "42rem", margin: "0 auto 4rem", textAlign: "center" }}>
                    <Reveal>
                        <Pill>Who Should Join</Pill>
                        <h2 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "clamp(1.75rem, 4vw, 3rem)", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.025em", marginTop: "1.5rem" }}>
                            Built for the people{" "}
                            <span style={{ background: "linear-gradient(135deg, oklch(0.5 0.25 300) 0%, oklch(0.65 0.27 325) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                                closest to the customer.
                            </span>
                        </h2>
                    </Reveal>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>
                    {WHO_ITEMS.map((label, i) => <WhoCard key={label} label={label} delay={i * 0.05} />)}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────
   Early Access Form
───────────────────────────────────────────── */
const FIELDS = [
    { id: "name", label: "Name", type: "text", placeholder: "Your full name", entry: "entry.332470159" },
    { id: "email", label: "Email", type: "email", placeholder: "you@company.com", entry: "entry.1837326874" },
    { id: "linkedin", label: "LinkedIn Profile", type: "url", placeholder: "linkedin.com/in/…", entry: "entry.461741123" },
    { id: "role", label: "Role", type: "text", placeholder: "VP Customer Success", entry: "entry.2075748482" },
    { id: "company", label: "Company", type: "text", placeholder: "Where you work", entry: "entry.1131519867" },
];

function EarlyAccess() {
    const [values, setValues] = useState({ name: "", email: "", linkedin: "", role: "", company: "" });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (id) => (e) => setValues((v) => ({ ...v, [id]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const body = new URLSearchParams();
        FIELDS.forEach(({ id, entry }) => body.append(entry, values[id]));
        await fetch(
            "https://docs.google.com/forms/d/e/1FAIpQLSdSxKehp8M8F1pPa8QW8AdVSrY1CZO28BrQmWrJmUDpjXSE2w/formResponse",
            { method: "POST", mode: "no-cors", body }
        );
        setLoading(false);
        setSubmitted(true);
    };

    return (
        <section id="early-access" style={{ position: "relative", padding: "7rem 1.5rem" }}>
            <div style={{ position: "absolute", left: "50%", top: "2.5rem", width: "48rem", height: "30rem", transform: "translateX(-50%)", background: "radial-gradient(circle, oklch(0.7 0.22 305 / 0.3), transparent 70%)", filter: "blur(80px)", opacity: 0.5, borderRadius: "50%", pointerEvents: "none" }} />
            <div style={{ maxWidth: "42rem", margin: "0 auto", position: "relative", zIndex: 1 }}>
                <div style={{ maxWidth: "42rem", margin: "0 auto 2.5rem", textAlign: "center" }}>
                    <Reveal>
                        <Pill>Early Access</Pill>
                        <h2 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "clamp(1.75rem, 4vw, 3rem)", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.025em", marginTop: "1.5rem" }}>
                            Be among the first to{" "}
                            <span style={{ background: "linear-gradient(135deg, oklch(0.5 0.25 300) 0%, oklch(0.65 0.27 325) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                                join.
                            </span>
                        </h2>
                        <p style={{ marginTop: "1.25rem", fontSize: "0.875rem", lineHeight: 1.65, color: "oklch(0.5 0.04 285)" }}>
                            Membership is by application. Tell us a little about yourself — we'll be in touch.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.1}>
                    <div style={{ background: "oklch(1 0 0 / 0.7)", border: "1px solid oklch(0.55 0.24 300 / 0.08)", borderRadius: "1.5rem", padding: "2rem", boxShadow: "0 4px 24px -8px oklch(0.4 0.15 295 / 0.12)" }}>
                        {submitted ? (
                            <div style={{ textAlign: "center", padding: "2.5rem 0" }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "3.5rem", height: "3.5rem", borderRadius: "50%", background: "linear-gradient(135deg, oklch(0.55 0.24 300), oklch(0.65 0.27 325))", color: "white", margin: "0 auto 1.5rem", boxShadow: "0 12px 40px -8px oklch(0.55 0.25 305 / 0.45)" }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 700 }}>Thank you for requesting access.</h3>
                                <p style={{ marginTop: "0.75rem", fontSize: "0.875rem", color: "oklch(0.5 0.04 285)", maxWidth: "24rem", marginLeft: "auto", marginRight: "auto" }}>
                                    We'll reach out soon as we open the next wave of members.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1.25rem" }}>
                                {FIELDS.map(({ id, label, type, placeholder }) => (
                                    <div key={id} style={{ display: "grid", gap: "0.5rem" }}>
                                        <label htmlFor={id} style={{ fontSize: "0.75rem", fontWeight: 500, color: "oklch(0.18 0.04 285 / 0.7)" }}>{label}</label>
                                        <input
                                            id={id}
                                            type={type}
                                            required
                                            maxLength={255}
                                            placeholder={placeholder}
                                            value={values[id]}
                                            onChange={handleChange(id)}
                                            style={{ height: "2.75rem", width: "100%", padding: "0 1rem", borderRadius: "0.75rem", border: "1px solid oklch(0.92 0.02 295)", background: "white", fontSize: "0.875rem", color: "oklch(0.18 0.04 285)", outline: "none", boxSizing: "border-box", transition: "all 0.3s" }}
                                            onFocus={e => { e.target.style.borderColor = "oklch(0.55 0.24 300 / 0.4)"; e.target.style.boxShadow = "0 0 0 4px oklch(0.55 0.24 300 / 0.1)"; }}
                                            onBlur={e => { e.target.style.borderColor = "oklch(0.92 0.02 295)"; e.target.style.boxShadow = "none"; }}
                                        />
                                    </div>
                                ))}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{ marginTop: "0.75rem", display: "inline-flex", alignItems: "center", justifyContent: "center", height: "3rem", padding: "0 1.75rem", background: "linear-gradient(135deg, oklch(0.55 0.24 300), oklch(0.65 0.27 325))", color: "white", boxShadow: "0 12px 40px -8px oklch(0.55 0.25 305 / 0.45)", borderRadius: "9999px", fontSize: "0.875rem", fontWeight: 600, border: "none", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, transition: "all 0.4s cubic-bezier(.22,1,.36,1)" }}
                                >
                                    {loading ? "Sending…" : "Request Access"}
                                </button>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>

            <div className="premiumSection">
                    <div className="premiumLine"></div>

                    <p className="premiumText">
                        Want priority access and exclusive member benefits?
                    </p>

                    <a className="premiumBtn" href="/premium-membership" target="_blank" rel="noopener noreferrer">
                        Become a Premium Member
                        <span>→</span>
                    </a>
                </div>

        </section>
    );
}

/* ─────────────────────────────────────────────
   Footer
───────────────────────────────────────────── */
function Footer() {
    return (
        <footer style={{ borderTop: "1px solid oklch(0.92 0.02 295 / 0.6)", padding: "3rem 1.5rem" }}>
            <div style={{ maxWidth: "72rem", margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "1.5rem" }}>
                <img src={LOGO_URL} alt="Consortia" style={{ height: "2.5rem", width: "auto" }} />
                <nav style={{ display: "flex", alignItems: "center", gap: "1.75rem", flexWrap: "wrap" }}>
                    {[
                        { href: "https://www.linkedin.com/company/customer-success-consortia/", label: "LinkedIn" },
                        { href: "https://www.instagram.com/customersuccessconsortia/", label: "Instagram" },
                        { href: "mailto:connect@customersuccessconsortia.com", label: "Email" },
                    ].map(({ href, label }) => (
                        <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                            style={{ fontSize: "0.875rem", fontWeight: 500, color: "oklch(0.5 0.04 285)", textDecoration: "none", transition: "color 0.3s" }}
                            onMouseEnter={e => e.target.style.color = "oklch(0.18 0.04 285)"}
                            onMouseLeave={e => e.target.style.color = "oklch(0.5 0.04 285)"}
                        >{label}</a>
                    ))}
                </nav>
                <p style={{ fontSize: "0.75rem", color: "oklch(0.5 0.04 285 / 0.7)" }}>© {new Date().getFullYear()} Consortia</p>
            </div>
        </footer>
    );
}




/* ─────────────────────────────────────────────
   Root App
───────────────────────────────────────────── */
export default function ComingSoon() {
    const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.get("join") === "true") {
            setShowEarlyAccessModal(true);
        }
    }, []);


    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body {
          font-family: "Inter", ui-sans-serif, system-ui, sans-serif;
          background: oklch(0.99 0.005 300);
          color: oklch(0.18 0.04 285);
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
          line-height: 1.5;
        }
        a { text-decoration: none; color: inherit; }
        img { max-width: 100%; display: block; }

        @keyframes consortia-ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        @keyframes consortia-float-slow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(15px, -20px); }
        }
        @keyframes consortia-float-slower {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-20px, 15px); }
        }

        .consortia-ping { animation: consortia-ping 1.5s cubic-bezier(0,0,0.2,1) infinite; }
        .consortia-float-slow { animation: consortia-float-slow 14s ease-in-out infinite; }
        .consortia-float-slower { animation: consortia-float-slower 18s ease-in-out infinite; }

        .consortia-nav-links {
          display: none;
          align-items: center;
          gap: 2rem;
        }
        @media (min-width: 768px) {
          .consortia-nav-links { display: flex; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

            <Nav />



            <main>
                <Hero />
                <About />
                <WhyConsortia />
                <WhoItsFor />
                <EarlyAccess />
                
            </main>
            <Footer />

            {showEarlyAccessModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button
                            className="close-btn"
                            onClick={() => setShowEarlyAccessModal(false)}
                        >
                            ✕
                        </button>

                        <EarlyAccess
                            onSuccess={() => {
                                setShowEarlyAccessModal(false);

                                setTimeout(() => {
                                    window.close();
                                }, 1500);
                            }}
                        />
                    </div>
                </div>
            )
            }
        </>
    );
}

